/**
 * Endpoint de alta manual de series (ingreso). En producción son series
 * que ya existen físicamente — el sistema solo las registra en el inventario
 * local. NO se informan al SNT en el momento del alta — el SNT se entera
 * recién cuando se despachan.
 *
 * Para agruparlas: cada batch lleva un `ingresoId` compartido (lo asigna
 * el endpoint). Esto permite eliminar el lote entero si fue creado por
 * error, antes de cualquier despacho posterior.
 */

import { z } from 'zod';
import { SerieSchema } from '@/api/contracts/series';
import { getDb, nextMovId } from '@/api/mock/db';
import { logApi } from '@/stores/apilog';
import { ApiError } from './auth';

const MOCK_LATENCY_MS = 200;
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const CrearIngresoRequest = z.object({
  gtin: z.string().regex(/^\d{14}$/, 'GTIN inválido'),
  lote: z.string().min(1, 'Lote requerido'),
  vencimiento: z.string().regex(/^\d{2}\/\d{4}$/, 'Vencimiento debe ser MM/YYYY'),
  ubicacionId: z.string().min(1),
  usuarioId: z.string().min(1),
  seriesIds: z.array(z.string().min(1)).min(1).max(10000),
  razon: z.string().optional(),
});

export const CrearIngresoResponse = z.object({
  ingresoId: z.string(),
  series: z.array(SerieSchema),
});

export type CrearIngresoRequest = z.infer<typeof CrearIngresoRequest>;
export type CrearIngresoResponse = z.infer<typeof CrearIngresoResponse>;

export async function crearIngreso(input: CrearIngresoRequest): Promise<CrearIngresoResponse> {
  const req = CrearIngresoRequest.parse(input);
  await delay(MOCK_LATENCY_MS);
  const db = getDb();

  // Validar que la ubicación existe y no es externa
  const ub = db.ubicaciones.find((u) => u.id === req.ubicacionId);
  if (!ub) throw new ApiError('Ubicación no encontrada', 'UB_NOT_FOUND');
  if (ub.externa) {
    throw new ApiError('No se puede crear alta en una ubicación externa', 'UB_EXTERNA');
  }

  // Validar que el producto existe
  if (!db.productos.find((p) => p.gtin === req.gtin)) {
    throw new ApiError(`GTIN ${req.gtin} no existe en el catálogo`, 'GTIN_NOT_FOUND');
  }

  // Duplicados internos
  const setIds = new Set(req.seriesIds);
  if (setIds.size !== req.seriesIds.length) {
    throw new ApiError('Hay series duplicadas en el batch', 'SERIES_DUPLICATE');
  }

  // Colisión con series ya existentes
  const existentes = req.seriesIds.filter((sid) => db.series.some((s) => s.id === sid));
  if (existentes.length > 0) {
    throw new ApiError(
      `${existentes.length} series ya existen (ej: ${existentes.slice(0, 3).join(', ')})`,
      'SERIES_COLLISION',
    );
  }

  const ahora = new Date().toISOString();
  const ingresoId =
    'ING-' +
    Date.now().toString(36).toUpperCase() +
    '-' +
    Math.random().toString(16).slice(2, 6).toUpperCase();

  const creadas = req.seriesIds.map((id) => {
    const serie = {
      id,
      gtin: req.gtin,
      lote: req.lote,
      vencimiento: req.vencimiento,
      tenencia: 'en_poder' as const,
      ubicacionActualId: req.ubicacionId,
      createdAt: ahora,
      altaManual: true,
      ingresoId,
    };
    db.series.push(serie);
    // Movimiento alta_manual SIN operacionId — derivarEstadoTrazaSerie lo trata
    // como `nunca_trazada` hasta que un despacho lo asocie a una operación.
    db.movimientos.push({
      id: nextMovId(),
      serieId: id,
      operacionId: null,
      tipoEvento: 'alta_manual',
      fecha: ahora,
      ubicacionId: req.ubicacionId,
      usuarioId: req.usuarioId,
      codigoTransaccion: null,
      eventoAnmatId: null,
      detalle: `Alta manual · Lote ${req.lote} · ${req.razon ?? 'sin detalle'}`,
      ingresoId,
    });
    return serie;
  });

  logApi({
    sistema: 'erp',
    metodo: 'POST',
    endpoint: '/api/series/alta-manual',
    status: 200,
    requestBody: {
      gtin: req.gtin,
      lote: req.lote,
      cantidad: req.seriesIds.length,
      ubicacionId: req.ubicacionId,
    },
    responseBody: { ingresoId, creadas: creadas.length },
  });

  return CrearIngresoResponse.parse({ ingresoId, series: creadas });
}

// =============================================================================
// ELIMINAR INGRESO (lote completo)
// =============================================================================

export const EliminarIngresoRequest = z.object({
  ingresoId: z.string().min(1),
  usuarioId: z.string().min(1),
  razon: z.string().min(1, 'Razón obligatoria para eliminar un lote'),
});

export const EliminarIngresoResponse = z.object({
  eliminadas: z.number().int(),
});

export type EliminarIngresoRequest = z.infer<typeof EliminarIngresoRequest>;
export type EliminarIngresoResponse = z.infer<typeof EliminarIngresoResponse>;

export async function eliminarIngreso(
  input: EliminarIngresoRequest,
): Promise<EliminarIngresoResponse> {
  const req = EliminarIngresoRequest.parse(input);
  await delay(MOCK_LATENCY_MS);
  const db = getDb();

  const seriesDelLote = db.series.filter((s) => s.ingresoId === req.ingresoId);
  if (seriesDelLote.length === 0) {
    throw new ApiError('Ingreso no encontrado o ya eliminado', 'INGRESO_NOT_FOUND');
  }

  // Defensa: no permitir eliminar si alguna serie ya tiene movimientos
  // adicionales (más allá del alta_manual) — implica que ya fue usada.
  const movsPorSerie = new Map<string, number>();
  for (const m of db.movimientos) {
    if (seriesDelLote.some((s) => s.id === m.serieId)) {
      movsPorSerie.set(m.serieId, (movsPorSerie.get(m.serieId) ?? 0) + 1);
    }
  }
  const usadas = seriesDelLote.filter((s) => (movsPorSerie.get(s.id) ?? 0) > 1);
  if (usadas.length > 0) {
    throw new ApiError(
      `${usadas.length} series del lote ya fueron usadas (despacho o pickeo) · no se puede eliminar`,
      'SERIES_IN_USE',
    );
  }

  // Eliminar series + movimientos del lote
  const idsSet = new Set(seriesDelLote.map((s) => s.id));
  db.series = db.series.filter((s) => !idsSet.has(s.id));
  db.movimientos = db.movimientos.filter((m) => !idsSet.has(m.serieId));

  logApi({
    sistema: 'erp',
    metodo: 'DELETE',
    endpoint: `/api/ingresos/${req.ingresoId}`,
    status: 200,
    requestBody: { motivo: req.razon },
    responseBody: { eliminadas: seriesDelLote.length },
  });

  return EliminarIngresoResponse.parse({ eliminadas: seriesDelLote.length });
}
